import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { ChatMessage, loadingByKey } from '../../types/Chat';

interface ChatState {
  chatId: string | null;
  messages: ChatMessage[];
  loadingByKey: Record<string, loadingByKey>;
  currentLoadingKey: string | null;
  createdAt: string | null;
  streamingChunks: Record<string, string>; // ← novo
}

const initialState: ChatState = {
  chatId: null,
  messages: [],
  loadingByKey: {},
  currentLoadingKey: null,
  createdAt: null,
  streamingChunks: {}, // ← novo
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatId(state, action: PayloadAction<string | null>) {
      state.chatId = action.payload;

      if (action.payload) {
        state.currentLoadingKey = action.payload;
      }
    },
    setMessages(state, action: PayloadAction<ChatMessage[]>) {
      state.messages = action.payload;
    },
    addMessages(state, action: PayloadAction<ChatMessage[]>) {
      state.messages = [...state.messages, ...action.payload];
    },
    setLoadingKey(
      state,
      action: PayloadAction<{
        key: string;
        status: 'streaming' | 'done';
        text?: string | null;
      }>,
    ) {
      const { key, status, text } = action.payload;
      const prev = state.loadingByKey[key] ?? { status: 'streaming', text: null };

      const next = { ...prev, status };

      if (Object.prototype.hasOwnProperty.call(action.payload, 'text')) {
        next.text = action.payload.text ?? null;
      }

      state.loadingByKey[key] = next;

      if (status === 'streaming') {
        state.currentLoadingKey = key;
      }

      if (status === 'done') {
        state.loadingByKey[key].text = null;
      }
    },

    setCreatedAt(state, action: PayloadAction<string | null>) {
      state.createdAt = action.payload;
    },
    setCurrentLoadingKey(state, action: PayloadAction<string | null>) {
      state.currentLoadingKey = action.payload;
    },
    clearChat(state) {
      state.chatId = null;
      state.messages = [];
      state.createdAt = null;
    },

    // Acumula token por token durante o streaming
    appendStreamChunk(
      state,
      action: PayloadAction<{ chatId: string; chunk: string }>,
    ) {
      const { chatId, chunk } = action.payload;
      state.streamingChunks[chatId] = (state.streamingChunks[chatId] ?? '') + chunk;
    },

    // Limpa o buffer quando o streaming termina (ao receber done: true)
    clearStreamChunk(state, action: PayloadAction<string>) {
      delete state.streamingChunks[action.payload];
    },
  },
});

export const {
  setChatId,
  setMessages,
  addMessages,
  setLoadingKey,
  setCurrentLoadingKey,
  setCreatedAt,
  clearChat,
  appendStreamChunk, // ← exportar
  clearStreamChunk, // ← exportar
} = chatSlice.actions;

export default chatSlice.reducer;
